import {
  ChangeDetectionStrategy,
  Component,
  input,
  InputSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartConfiguration, ChartType } from 'chart.js';
import { IIncident } from '@web/dtos';
import { BaseChartDirective } from 'ng2-charts';

@Component({
  selector: 'aw-incident-chart',
  imports: [CommonModule, BaseChartDirective],
  templateUrl: './incident-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncidentChartComponent {
  incidents: InputSignal<IIncident[]> = input.required();

  barChartType: ChartType = 'bar';
  barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    scales: {
      x: {
        stacked: true,
        title: { display: true, text: 'Datum' },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: { display: true, text: 'Ereignisanzahl' },
      },
    },
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'Ereignisse pro Tag pro Code' },
    },
  };

  get barChartData(): ChartConfiguration['data'] {
    const byDayAndCode: Record<string, Record<string, number>> = {};
    const uniqueDates = new Set<string>();
    const uniqueCodes = new Set<string>();

    for (const incident of this.incidents()) {
      const date: string = incident.createdAt
        ? new Date(incident.createdAt).toISOString().split('T')[0]
        : 'Unknown';

      const code: string = incident.code;

      uniqueDates.add(date);
      uniqueCodes.add(code);

      byDayAndCode[date] ??= {};
      byDayAndCode[date][code] = (byDayAndCode[date][code] ?? 0) + 1;
    }

    const sortedDates: string[] = Array.from(uniqueDates).sort(); // chronological order
    const codes: string[] = Array.from(uniqueCodes);

    const datasets = codes.map((code: string, index: number) => {
      const backgroundColor: string = this.getColor(index);

      return {
        label: code,
        data: sortedDates.map(
          (date: string): number => byDayAndCode[date]?.[code] ?? 0
        ),
        backgroundColor,
      };
    });

    return {
      labels: sortedDates,
      datasets,
    };
  }

  // Simple color palette
  private getColor(index: number): string {
    const palette: string[] = [
      'rgba(54, 162, 235, 0.6)',
      'rgba(255, 99, 132, 0.6)',
      'rgba(255, 206, 86, 0.6)',
      'rgba(75, 192, 192, 0.6)',
      'rgba(153, 102, 255, 0.6)',
      'rgba(255, 159, 64, 0.6)',
    ];
    return palette[index % palette.length];
  }
}
